import {useEffect, useState} from 'react';

import {GatewayError} from '@gravity-ui/gateway';
import {ValidationType} from 'common/constants/survey';
import {SurveySettings, SurveyValues} from 'common/models/survey';
import {SubmitError} from 'common/models/system';
import {UseFormReturn} from 'react-hook-form';
import i18n from 'ui/i18n';
import focusByNames from 'ui/utils/focusByNames';
import getSeriesQuestionSlug from 'ui/utils/getSeriesQuestionSlug';
import {errorMessageByType} from 'ui/utils/validations';

export const findErrorPage = (name: string, survey: SurveySettings) => {
    const itemName = name.includes('.') ? name.split('.')?.[0] : name;
    const pageIndex = survey.pages.findIndex((page) =>
        page.items.some((item) => item.id === itemName),
    );
    return pageIndex === -1 ? 1 : pageIndex + 1;
};

const useSurveyErrors = (
    methods: UseFormReturn<SurveyValues>,
    setPage: (page: number) => void,
    survey?: SurveySettings,
): [(canFocus: boolean) => void, (error: GatewayError) => void] => {
    //to scroll and focus once per submission
    const [canFocus, setCanFocus] = useState(true);

    useEffect(() => {
        if (methods.formState.errors && canFocus) {
            const names: string[] = [];
            Object.entries(methods.formState.errors).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((series, index) => {
                        Object.keys(series).forEach((question) => {
                            names.push(getSeriesQuestionSlug(key, index, question));
                        });
                    });
                } else {
                    names.push(key);
                }
            });
            const focused = focusByNames(names);
            if (focused) {
                setCanFocus(false);
            }
        }
    }, [methods.formState, canFocus]);

    const setErrors = (error: GatewayError) => {
        if (
            error.details?.submitErrors &&
            Array.isArray(error.details.submitErrors) &&
            error.details.submitErrors.length > 0
        ) {
            (error.details.submitErrors as SubmitError[]).forEach((item) => {
                const commonMessage = errorMessageByType[item.type as ValidationType];
                const message = commonMessage ? commonMessage : item.message;
                let question = item.name;
                if (item.series && item.index !== undefined && item.name) {
                    question = getSeriesQuestionSlug(item.series, item.index, item.name);
                }
                methods.setError(question ?? 'root.serverError', {
                    message,
                    type: error.code,
                });
            });

            const errorQuestion = (error.details.submitErrors as SubmitError[]).find((error) =>
                Boolean(error.name),
            )?.name;

            if (errorQuestion && survey) {
                const errorPage = findErrorPage(errorQuestion, survey);
                setPage(errorPage);
            }
        } else {
            methods.setError('root.serverError', {
                message: i18n('survey-page', 'submit-error'),
                type: error.code,
            });
        }
    };

    return [setCanFocus, setErrors];
};
export default useSurveyErrors;
